import React from 'react';
import { EVENT_TEAM_ROLE } from '../../../constants/AppraiserEventTeamRoleType';
import StyledTable from '@shared/components/StyledTable';
import MuiInputNumber from '@shared/components/MuiInputNumber';
import { NOTES_INPUT_MAXLENGTH } from '../../../constants/AppraiserEventInputConstants';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import { TEAM_TABLE_TYPE } from '../../../constants/AppraiserAssignmentTeamTableType';
import EventAssignmentTableWrapper from './EventAssignmentTableWrapper';
import { showWarningToast } from '@shared/services/toastService';
import { APPRAISER_EVENT_TYPE } from '../../../constants/AppraiserEventType';
import { chain } from 'lodash';

const TABLE_FIELDS = {
  NOTES: 'notes',
  NUMBER_OF_PEOPLE: 'numberOfPeople'
};

const TABLE_TITLE_MAP = {
  [EVENT_TEAM_ROLE.ISACA]: 'ISACA ATM(s)',
  [EVENT_TEAM_ROLE.INTERNAL]: 'Internal ATM(s)',
  [EVENT_TEAM_ROLE.OBSERVATION]: 'Observation(s)',
  [EVENT_TEAM_ROLE.APPRENTICESHIP]: 'Apprenticeship(s)',
  [EVENT_TEAM_ROLE.FDA]: 'FDA ATM(s)'
};

const OBSERVABLE_EVENT_TEAM_ROLES = [EVENT_TEAM_ROLE.OBSERVATION, EVENT_TEAM_ROLE.APPRENTICESHIP];

const WARNING_MESSAGE =
  'ISACA/MDDAP must contract an observer/coach. Please ensure a member of the team is qualified to fulfill this role.';

/**
 * @typedef {object} EventTeamTablesProps
 * @property {Array<*>} [eventTeams]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} [setEventFormData]
 * @property {boolean} [loading]
 * @property {Array<*>} [eventTeamErrors]
 * @property {boolean} [isDisabledTeamTables]
 * @property {boolean} [hideNotesColumn]
 * @property {boolean} [showEventAssignment]
 * @property {Array<*>} [eventAssignments]
 * @property {*} [eventType]
 * @property {boolean} [showUnavailableAtms]
 * @property {boolean} [showAssignmentRank]
 * @property {boolean} [canAssignATMs]
 */

/**
 * @param {EventTeamTablesProps} props
 */
const EventTeamTables = props => {
  const {
    eventTeamErrors,
    eventTeams = [],
    isDisabledTeamTables = false,
    hideNotesColumn = false,
    showEventAssignment = false,
    eventAssignments = [],
    loading = false,
    setEventFormData,
    eventType = APPRAISER_EVENT_TYPE.REGULAR,
    showUnavailableAtms = false,
    showAssignmentRank = false,
    canAssignATMs = false
  } = props;

  const tableData = eventTeams.map(x => ({
    key: x.role,
    title: TABLE_TITLE_MAP[x.role],
    numberOfPeople: x.numberOfPeople,
    notes: x.notes || ''
  }));

  const coreTeamTableData = tableData.filter(
    x => x.key === EVENT_TEAM_ROLE.ISACA || x.key === EVENT_TEAM_ROLE.INTERNAL
  );

  const extrasTableData = tableData.filter(
    x =>
      x.key === EVENT_TEAM_ROLE.OBSERVATION ||
      x.key === EVENT_TEAM_ROLE.APPRENTICESHIP ||
      x.key === EVENT_TEAM_ROLE.FDA
  );

  const columns = [
    {
      title: 'MDDAP Role',
      dataIndex: 'title',
      width: '20%',
      render: text => text
    },
    {
      title: '# of people',
      dataIndex: TABLE_FIELDS.NUMBER_OF_PEOPLE,
      width: '20%',
      render: (value, record) => (
        <MuiInputNumber
          dataTestId={`${record.key}-numberOfPeople`}
          value={value}
          disabled={isDisabledTeamTables}
          error={hasError(eventTeamErrors, record.key, TABLE_FIELDS.NUMBER_OF_PEOPLE)}
          handleChange={newValue => {
            handleNumberOfPeopleChange(record.key, newValue);
          }}
        />
      )
    },
    {
      title: 'Notes',
      dataIndex: TABLE_FIELDS.NOTES,
      width: '60%',
      render: (value, record) => (
        <MuiInputTextWithRemainingChars
          className='input-text'
          data-testid={`${record.key}-notes`}
          type='text'
          value={value}
          disabled={isDisabledTeamTables}
          error={hasError(eventTeamErrors, record.key, TABLE_FIELDS.NOTES)}
          disableUnderline
          style={{
            padding: '1rem'
          }}
          onChange={e => {
            setValue(TABLE_FIELDS.NOTES, record.key, e.target.value);
          }}
          maxLength={NOTES_INPUT_MAXLENGTH}
          isInlineTable={true}
        />
      ),
      hidden: hideNotesColumn
    }
  ].filter(item => !item.hidden);

  const handleNumberOfPeopleChange = (role, value) => {
    if (
      eventType === APPRAISER_EVENT_TYPE.DIRECT_CHANNEL &&
      shouldShowObserverApprenticeWarning(eventTeams, role, value)
    ) {
      showWarningToast(WARNING_MESSAGE);
    }

    setValue(TABLE_FIELDS.NUMBER_OF_PEOPLE, role, value);
  };

  const setValue = (field, role, value) => {
    const updatedTeams = [...eventTeams];
    updatedTeams.find(x => x.role === role)[field] = value;

    setEventFormData(prevState => ({ ...prevState, eventTeams: updatedTeams }));
  };

  return (
    <>
      <section className='page-section'>
        <div className='section-description'>
          <span className='section-title bold'>Core Team</span>
          <p>
            Enter the minimum number of ISACA ATMs required to deliver the appraisal. Enter the
            number of Internal ATMs the site intends to use on the appraisal. As a reminder,
            Internal ATMs can replace an ISACA ATM on an appraisal if they are fully qualified.
            Include the names of any Internal ATMs in the Notes section.
          </p>
        </div>
        <StyledTable
          className='event-team-table'
          columns={columns}
          dataSource={coreTeamTableData}
          pagination={false}
          showSorterTooltip={false}
          loading={loading}
        />
      </section>

      {showEventAssignment && (
        <EventAssignmentTableWrapper
          tableType={TEAM_TABLE_TYPE.CORE}
          eventTeams={eventTeams}
          showUnavailableAtms={showUnavailableAtms}
          showAssignmentRank={showAssignmentRank}
          canAssignATMs={canAssignATMs}
          setEventFormData={setEventFormData}
          eventAssignments={eventAssignments}
        />
      )}

      <section className='page-section'>
        <div className='section-description'>
          <span className='section-title bold'>Extras</span>
          <p>
            Enter the number of ISACA and/or Internal ATM candidate observations, ISACA and/or
            Internal ATM apprenticeships, and FDA ATMs that can be included on the appraisal.
            Maximum number of two “extras” per appraisal. Include the names of any Internal ATM
            observation candidates or apprentices in the Notes section. ISACA reserves the right to
            have an ISACA candidate observation or an ISACA apprentice over an Internal ATM
            observation or Internal ATM apprentice.
          </p>
        </div>
        <StyledTable
          className='event-team-table'
          columns={columns}
          dataSource={extrasTableData}
          pagination={false}
          showSorterTooltip={false}
          loading={loading}
        />
      </section>

      {showEventAssignment && (
        <EventAssignmentTableWrapper
          tableType={TEAM_TABLE_TYPE.EXTRA}
          eventTeams={eventTeams}
          setEventFormData={setEventFormData}
          eventAssignments={eventAssignments}
          showUnavailableAtms={showUnavailableAtms}
          showAssignmentRank={showAssignmentRank}
          canAssignATMs={canAssignATMs}
        />
      )}
    </>
  );
};

const hasError = (errors, index, field) => errors?.[index]?.[field] === true;

const shouldShowObserverApprenticeWarning = (currentEventTeams, role, newValue) => {
  if (!OBSERVABLE_EVENT_TEAM_ROLES.includes(role)) {
    return false;
  }

  const totalObserverApprenticePeople = chain(currentEventTeams)
    .filter(x => OBSERVABLE_EVENT_TEAM_ROLES.includes(x.role))
    .sumBy(team => team.numberOfPeople || 0)
    .value();

  return totalObserverApprenticePeople === 0 && newValue > 0;
};

export default EventTeamTables;
